import io from "socket.io-client";
import api from "../services/api";
import { getBackendUrl } from "../config";

const getAuthToken = () => {
  const auth = api.defaults.headers.Authorization || "";
  if (!auth) return "";
  return auth.startsWith("Bearer ") ? auth : `Bearer ${auth}`;
};

class SocketWorker {
  constructor(companyId, userId) {
    const normalizedCompanyId = companyId ? String(companyId) : "";
    const normalizedUserId = userId ? String(userId) : "";

    if (SocketWorker.instance) {
      const existing = SocketWorker.instance;
      const sameSession =
        existing.companyId === normalizedCompanyId &&
        existing.userId === normalizedUserId;

      if (sameSession && existing.socket) {
        existing.updateAuthToken();
        return existing;
      }

      existing.destroy();
    }

    this.companyId = normalizedCompanyId;
    this.userId = normalizedUserId;
    this.token = getAuthToken();
    this.socket = null;
    this.eventListeners = {};
    this.configureSocket();
    SocketWorker.instance = this;

    return SocketWorker.instance;
  }

  updateAuthToken() {
    this.token = getAuthToken();
    if (this.socket?.io?.opts?.query) {
      this.socket.io.opts.query.token = this.token;
    }
  }

  configureSocket() {
    this.updateAuthToken();

    const backendUrl = getBackendUrl();
    if (!backendUrl || !this.companyId) {
      return;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
    }

    this.socket = io(`${backendUrl}/${this.companyId}`, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: Infinity,
      withCredentials: true,
      transports: ["polling", "websocket"],
      query: { userId: this.userId, token: this.token },
    });

    this.socket.on("connect", () => {
      console.log("Conectado ao servidor Socket.IO");
    });

    this.socket.on("disconnect", (reason) => {
      console.log("Desconectado do servidor Socket.IO:", reason);
      if (reason === "io server disconnect") {
        this.reconnectAfterDelay();
      }
    });

    this.socket.io.on("reconnect_attempt", () => {
      this.updateAuthToken();
    });
  }

  on(event, callback) {
    this.connect();
    if (!this.socket) return;

    this.socket.on(event, callback);

    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  emit(event, data) {
    this.connect();
    if (!this.socket) return;
    this.socket.emit(event, data);
  }

  off(event, callback) {
    this.connect();
    if (!this.socket || !this.eventListeners[event]) return;

    if (callback) {
      this.socket.off(event, callback);
      this.eventListeners[event] = this.eventListeners[event].filter(
        (cb) => cb !== callback
      );
      if (this.eventListeners[event].length === 0) {
        delete this.eventListeners[event];
      }
      return;
    }

    this.eventListeners[event].forEach((cb) => this.socket.off(event, cb));
    delete this.eventListeners[event];
  }

  destroy() {
    if (this.socket) {
      Object.keys(this.eventListeners).forEach((event) => {
        this.eventListeners[event].forEach((cb) => {
          this.socket.off(event, cb);
        });
      });
      this.eventListeners = {};
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }

    if (SocketWorker.instance === this) {
      SocketWorker.instance = null;
    }
  }

  disconnect() {
    this.destroy();
  }

  reconnectAfterDelay() {
    setTimeout(() => {
      this.updateAuthToken();

      if (this.socket && !this.socket.connected) {
        this.socket.connect();
        return;
      }

      if (!this.socket) {
        this.configureSocket();
      }
    }, 1000);
  }

  connect() {
    if (!this.socket) {
      this.configureSocket();
    }
  }

  get connected() {
    return Boolean(this.socket?.connected);
  }
}

const instance = (companyId, userId) => new SocketWorker(companyId, userId);

export default instance;
